/**
 * Captcha del checkout — Cloudflare Turnstile.
 *
 * Mitigación del hallazgo H05 del ethical hacking. El backend valida el token en
 * el header `recaptcha-token` (middleware ValidateReCaptchaV3Token, cuyo endpoint
 * de verificación apunta a Turnstile por configuración).
 *
 * Se eligió Turnstile por sobre reCAPTCHA porque Google apagó el servicio clásico
 * y su reemplazo exige proyecto de Google Cloud con facturación y limita el cupo
 * gratuito a 10.000 verificaciones mensuales: como el captcha protege justamente
 * el endpoint que se enumera, bajo un ataque el propio control empezaría a
 * costarnos dinero.
 *
 * Es INERTE mientras no exista REACT_APP_RECAPTCHA_SITE_KEY: no carga el script
 * y devuelve null, así que el checkout se comporta igual que sin captcha.
 *
 * Nunca lanza ni bloquea: si el script no carga, si el widget falla o si demora
 * demasiado, devuelve null y la petición sale igual. El backend también falla
 * abierto, de modo que una caída del verificador no puede dejar a nadie sin poder
 * pagar.
 */

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const SCRIPT_ID = 'cf-turnstile';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TIMEOUT_MS = 8000;

type TurnstileOpts = {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'timeout-callback'?: () => void;
  appearance?: 'always' | 'execute' | 'interaction-only';
};

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement | string, opts: TurnstileOpts) => string | undefined;
      remove: (widgetId: string) => void;
    };
  }
}

let cargando: Promise<void> | null = null;

function cargarScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (cargando) return cargando;

  cargando = new Promise<void>((resolve, reject) => {
    const existente = document.getElementById(SCRIPT_ID);
    if (existente) {
      existente.addEventListener('load', () => resolve());
      existente.addEventListener('error', () => reject(new Error('turnstile no cargó')));
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile no cargó'));
    document.head.appendChild(s);
  });

  return cargando;
}

/**
 * Token de Turnstile, o null si no aplica o falla.
 *
 * Turnstile resuelve por callback sobre un contenedor, no con una promesa como
 * reCAPTCHA. Se monta un contenedor oculto y se limpia siempre, pase lo que pase.
 */
export async function obtenerTokenCaptcha(): Promise<string | null> {
  if (!SITE_KEY) return null;

  try {
    await cargarScript();
    if (!window.turnstile) return null;

    const contenedor = document.createElement('div');
    contenedor.style.display = 'none';
    document.body.appendChild(contenedor);

    let widgetId: string | undefined;
    const limpiar = () => {
      try {
        if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      } catch {
        /* el widget ya no existe */
      }
      contenedor.remove();
    };

    return await new Promise<string | null>((resolve) => {
      // Red de seguridad: si el widget nunca responde, seguimos sin token en vez
      // de dejar el formulario colgado.
      const reloj = setTimeout(() => resolve(null), TIMEOUT_MS);
      const terminar = (token: string | null) => {
        clearTimeout(reloj);
        resolve(token);
      };

      widgetId = window.turnstile!.render(contenedor, {
        sitekey: SITE_KEY,
        appearance: 'interaction-only', // solo se muestra si exige interacción
        callback: (token: string) => terminar(token),
        'error-callback': () => terminar(null),
        'timeout-callback': () => terminar(null),
      });

      if (!widgetId) terminar(null);
    }).finally(limpiar);
  } catch {
    return null; // el backend falla abierto: no bloqueamos el pago
  }
}

/** Header listo para axios. Vacío si no hay token. */
export async function headerRecaptcha(_action?: string): Promise<Record<string, string>> {
  const token = await obtenerTokenCaptcha();
  return token ? { 'recaptcha-token': token } : {};
}
